// FaceLock Ink Scan lock-on, face help: where the named place is on a face,
// from the face model the Scan already ships (vision.ts), so the lock picks the
// right tattoo on a face with many (owner, 2026-10-07: he has many face
// tattoos) and never takes the wrong side. Pure geometry, no DOM.
//
// A fix is made from one detection of the 478 face points (normalised to the
// RAW camera frame, the same space as the look copy; geometry.ts). It gives:
//   * the midline (nose bridge 168 to chin 152) and its normal, signed so +
//     is the person's OWN left (the side of their left eye, 263). Read from
//     the points, so a mirrored or turned camera gives the same sign;
//   * a look-size mask the finder never counts as ink: the eyes, the brows,
//     the lips, the nostrils and (for face places) everything off the face,
//     or (for neck places) the face itself. A part the place is ON is not
//     masked (beside the eye: the eye itself up to its outer corner, nothing
//     past it; the brow hairs but not the skin round them for a brow, the
//     lips but not round them for the mouth places, no nostrils for the nose
//     places, no face outline for the temples, jaw and sideburns);
//   * the anchor: the named spot (the finder's hint). Neck and most head
//     places have none: their side is checked, nothing more.
// When there is no fix (too close for the model to see a face, or the model
// is off) the Scan carries on with pixels alone: the face help never blocks.

import type { Landmark } from "./geometry";
import type { Box01 } from "./ink-find";
import { thumbDiff } from "./ink";

/** A point in look px. */
export type P = { x: number; y: number };

export interface FaceFix {
  /** When the face was seen (the look clock, ms). */
  t: number;
  /** The look size the fix (and its mask) is in, px. */
  w: number;
  h: number;
  /** Outer eye corner to outer eye corner (33 to 263), look px: the face's own unit. */
  iod: number;
  /** The midline: nose bridge (168) to chin (152). */
  a: P;
  b: P;
  /** Unit normal to the midline, toward the person's LEFT. */
  n: P;
  /** 1 = never ink (w x h). */
  mask: Uint8Array;
  /** The named spot, or null (neck and head places but the ears). */
  anchor: P | null;
  /** The guide thumbnail when the face was seen: a fix is used only while the picture has not moved. */
  thumb: number[];
}

export const FACE_FIX = {
  /** A fix older than this is not used (the face moved). */
  maxAgeMs: 1200,
  /** Nor once the guide thumbnail has changed this much since it. */
  maxThumbDiff: 0.06,
  /** A face smaller than this share of the look width is too far to place a mark on. */
  minIod: 0.04,
  /** The anchor's box (the finder's hint): a square this many IODs wide. */
  anchorSide: 1.2,
} as const;

/** MediaPipe's face outline, in order (as components/chopbox/engine/fx/landmarks.ts). */
export const FACE_OVAL = [10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109] as const;
const LIPS_OUTER = [61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291, 375, 321, 405, 314, 17, 84, 181, 91, 146] as const;
/** Brows as strips: the upper row outer to inner, then the lower row inner to outer. */
const BROW_RIGHT = [70, 63, 105, 66, 107, 55, 65, 52, 53, 46] as const;
const BROW_LEFT = [300, 293, 334, 296, 336, 285, 295, 282, 283, 276] as const;

/** Mask sizes, in IODs. */
const MASK = { eyeAlong: 0.2, eyeAcross: 0.1, iris: 0.1, browGrow: 0.04, lipGrow: 0.03, nostril: 0.07, ovalScale: 1.12 } as const;

type FaceRegion = "face" | "neck" | "head";
/** pt(i): face point i in look px; u: unit down the midline; n: unit toward the person's left; iod. */
type AnchorFn = (pt: (i: number) => P, u: P, n: P, iod: number) => P;

const lerp = (p: P, q: P, k: number): P => ({ x: p.x + (q.x - p.x) * k, y: p.y + (q.y - p.y) * k });
const mid = (p: P, q: P): P => lerp(p, q, 0.5);
const plus = (p: P, d: P, k: number): P => ({ x: p.x + d.x * k, y: p.y + d.y * k });
const unit = (d: P): P => {
  const l = Math.hypot(d.x, d.y) || 1;
  return { x: d.x / l, y: d.y / l };
};
const away = (p: P, from: P, k: number): P => plus(p, unit({ x: p.x - from.x, y: p.y - from.y }), k);

/**
 * Every face, neck and head place (shared/face-qc/marks.mjs; a test keeps the
 * two equal): how its picture is masked (its region) and its anchor. The
 * anchors were checked on the two recorded 478-point faces: each left one
 * sits at least 0.18 IOD on the person's left of the midline, each right one
 * as far on their right, each centre one within 0.10 of it.
 */
export const INK_FACE_PLACES: Readonly<Record<string, { region: FaceRegion; anchor: AnchorFn | null }>> = Object.freeze({
  head_top: { region: "head", anchor: null },
  head_left: { region: "head", anchor: null },
  head_right: { region: "head", anchor: null },
  // The places added for face tattoos (owner, 2026-10-07): the ears (0.3 IOD out from the face's edge at 454 / 234),
  // the sideburns, the sides of the forehead and the nose, the cheekbones, beside the mouth's corners, below the lip.
  ear_left: { region: "head", anchor: (pt, _u, n, iod) => plus(pt(454), n, 0.3 * iod) },
  ear_right: { region: "head", anchor: (pt, _u, n, iod) => plus(pt(234), n, -0.3 * iod) },
  behind_ear_left: { region: "head", anchor: null },
  behind_ear_right: { region: "head", anchor: null },
  sideburn_left: { region: "face", anchor: (pt) => mid(pt(454), pt(356)) },
  sideburn_right: { region: "face", anchor: (pt) => mid(pt(234), pt(127)) },
  head_back: { region: "head", anchor: null },
  forehead: { region: "face", anchor: (pt) => pt(151) },
  forehead_left: { region: "face", anchor: (pt) => mid(pt(151), pt(332)) },
  forehead_right: { region: "face", anchor: (pt) => mid(pt(151), pt(103)) },
  between_brows: { region: "face", anchor: (pt) => pt(9) },
  brow_left: { region: "face", anchor: (pt) => mid(pt(334), pt(296)) },
  brow_right: { region: "face", anchor: (pt) => mid(pt(105), pt(66)) },
  temple_left: { region: "face", anchor: (pt) => lerp(pt(263), pt(356), 0.6) },
  temple_right: { region: "face", anchor: (pt) => lerp(pt(33), pt(127), 0.6) },
  // Beside the eye, at its outer corner: a quarter of the eye's width out from 263 (33 on the right).
  eye_left: { region: "face", anchor: (pt) => lerp(pt(263), pt(362), -0.25) },
  eye_right: { region: "face", anchor: (pt) => lerp(pt(33), pt(133), -0.25) },
  under_eye_left: { region: "face", anchor: (pt) => lerp(pt(374), pt(280), 0.45) },
  under_eye_right: { region: "face", anchor: (pt) => lerp(pt(145), pt(50), 0.45) },
  // Halfway from the outer eye corner to the face's edge, 0.2 IOD down the midline.
  cheekbone_left: { region: "face", anchor: (pt, u, _n, iod) => plus(mid(pt(263), pt(454)), u, 0.2 * iod) },
  cheekbone_right: { region: "face", anchor: (pt, u, _n, iod) => plus(mid(pt(33), pt(234)), u, 0.2 * iod) },
  cheek_left: { region: "face", anchor: (pt) => pt(280) },
  cheek_right: { region: "face", anchor: (pt) => pt(50) },
  nose: { region: "face", anchor: (pt) => mid(pt(168), pt(1)) },
  // The nose wings (the Face tool's noseLeftWing / noseRightWing): 278 / 48 sat only 0.178 IOD off the midline on the recorded female face.
  nose_left: { region: "face", anchor: (pt) => pt(358) },
  nose_right: { region: "face", anchor: (pt) => pt(129) },
  lip: { region: "face", anchor: (pt) => lerp(pt(0), pt(1), 0.35) },
  // 0.15 IOD out from the mouth's corner (291 left, 61 right), away from the other corner.
  mouth_left: { region: "face", anchor: (pt, _u, _n, iod) => away(pt(291), pt(61), 0.15 * iod) },
  mouth_right: { region: "face", anchor: (pt, _u, _n, iod) => away(pt(61), pt(291), 0.15 * iod) },
  below_lip: { region: "face", anchor: (pt) => lerp(pt(17), pt(152), 0.4) },
  chin: { region: "face", anchor: (pt) => lerp(pt(152), pt(17), 0.35) },
  jaw_left: { region: "face", anchor: (pt) => pt(397) },
  jaw_right: { region: "face", anchor: (pt) => pt(172) },
  neck_front: { region: "neck", anchor: null },
  neck_left: { region: "neck", anchor: null },
  neck_right: { region: "neck", anchor: null },
  neck_back: { region: "neck", anchor: null },
});

// ------------------------------------------------------------ drawing --
// Masks are drawn at pixel centres (x + 0.5, y + 0.5), once per fix.

/** Fills the polygon (even-odd), row by row; outside = fill everything NOT in it. */
function fillPolygon(m: Uint8Array, w: number, h: number, pts: readonly P[], outside = false) {
  const xs: number[] = [];
  for (let y = 0; y < h; y++) {
    const cy = y + 0.5;
    xs.length = 0;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      const p = pts[i];
      const q = pts[j];
      if ((p.y > cy) !== (q.y > cy)) xs.push(p.x + ((cy - p.y) * (q.x - p.x)) / (q.y - p.y));
    }
    xs.sort((a, b) => a - b);
    const row = y * w;
    if (outside) {
      let from = 0;
      for (let k = 0; k + 1 < xs.length; k += 2) {
        const x0 = Math.max(0, Math.min(w, Math.ceil(xs[k] - 0.5)));
        for (let x = from; x < x0; x++) m[row + x] = 1;
        from = Math.max(from, Math.max(0, Math.min(w, Math.ceil(xs[k + 1] - 0.5))));
      }
      for (let x = from; x < w; x++) m[row + x] = 1;
    } else {
      for (let k = 0; k + 1 < xs.length; k += 2) {
        const x0 = Math.max(0, Math.ceil(xs[k] - 0.5));
        const x1 = Math.min(w, Math.ceil(xs[k + 1] - 0.5));
        for (let x = x0; x < x1; x++) m[row + x] = 1;
      }
    }
  }
}

/** Every pixel within r of the segment p-q. */
function fillCapsule(m: Uint8Array, w: number, h: number, p: P, q: P, r: number) {
  const x0 = Math.max(0, Math.floor(Math.min(p.x, q.x) - r));
  const x1 = Math.min(w - 1, Math.ceil(Math.max(p.x, q.x) + r));
  const y0 = Math.max(0, Math.floor(Math.min(p.y, q.y) - r));
  const y1 = Math.min(h - 1, Math.ceil(Math.max(p.y, q.y) + r));
  const dx = q.x - p.x;
  const dy = q.y - p.y;
  const l2 = dx * dx + dy * dy || 1;
  const r2 = r * r;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const px = x + 0.5 - p.x;
      const py = y + 0.5 - p.y;
      const k = Math.max(0, Math.min(1, (px * dx + py * dy) / l2));
      const ex = px - k * dx;
      const ey = py - k * dy;
      if (ex * ex + ey * ey <= r2) m[y * w + x] = 1;
    }
  }
}

/** The polygon, grown by r (0: as drawn). */
function fillShape(m: Uint8Array, w: number, h: number, pts: readonly P[], r: number) {
  fillPolygon(m, w, h, pts);
  if (r > 0) for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) fillCapsule(m, w, h, pts[j], pts[i], r);
}

/** An ellipse round c: semi-axis a along the unit d, b across it; with `from`, only where it is at least `from` along d (px). */
function fillEllipse(m: Uint8Array, w: number, h: number, c: P, d: P, a: number, b: number, from = -Infinity) {
  const r = Math.max(a, b);
  const x0 = Math.max(0, Math.floor(c.x - r));
  const x1 = Math.min(w - 1, Math.ceil(c.x + r));
  const y0 = Math.max(0, Math.floor(c.y - r));
  const y1 = Math.min(h - 1, Math.ceil(c.y + r));
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const px = x + 0.5 - c.x;
      const py = y + 0.5 - c.y;
      const at = px * d.x + py * d.y;
      if (at < from) continue;
      const along = at / a;
      const across = (py * d.x - px * d.y) / b;
      if (along * along + across * across <= 1) m[y * w + x] = 1;
    }
  }
}

const fillDisc = (m: Uint8Array, w: number, h: number, c: P, r: number) => fillEllipse(m, w, h, c, { x: 1, y: 0 }, r, r);

/** The outline scaled k times round its centroid. */
export function ovalOf(pt: (i: number) => P, k = 1): P[] {
  const o = FACE_OVAL.map(pt);
  const c = o.reduce((s, p) => ({ x: s.x + p.x / o.length, y: s.y + p.y / o.length }), { x: 0, y: 0 });
  return o.map((p) => ({ x: c.x + (p.x - c.x) * k, y: c.y + (p.y - c.y) * k }));
}

/** What the finder must not count as ink for a mark at `place`. */
function maskOf(place: string, region: FaceRegion, pt: (i: number) => P, iod: number, w: number, h: number): Uint8Array {
  const m = new Uint8Array(w * h);
  if (region === "head") return m;
  if (region === "neck") {
    fillPolygon(m, w, h, ovalOf(pt));
    return m;
  }
  // The eyes. Beside an eye: the eye only up to its outer corner, nothing past it (the ink is at the corner, often on
  // the lid line). With only the iris masked, the white, the lids and the lashes joined the corner tattoo's group and
  // the finder dropped it as an eye at every stage: "Beside the left eye (outer corner)" never locked (2026-10-07
  // review).
  const eye = (outer: number, inner: number, iris: number, beside: boolean) => {
    const p = pt(outer);
    const q = pt(inner);
    const d = unit({ x: q.x - p.x, y: q.y - p.y });
    const half = Math.hypot(q.x - p.x, q.y - p.y) / 2;
    fillEllipse(m, w, h, mid(p, q), d, MASK.eyeAlong * iod, MASK.eyeAcross * iod, beside ? -half : -Infinity);
    if (beside) fillDisc(m, w, h, pt(iris), MASK.iris * iod);
  };
  eye(263, 362, 473, place === "eye_left");
  eye(33, 133, 468, place === "eye_right");
  // The brows: grown a little, except the hairs only on the brow the mark is on.
  fillShape(m, w, h, BROW_LEFT.map(pt), place === "brow_left" ? 0 : MASK.browGrow * iod);
  fillShape(m, w, h, BROW_RIGHT.map(pt), place === "brow_right" ? 0 : MASK.browGrow * iod);
  // The lips: grown a little, except for marks on or round the mouth.
  const nearMouth = place === "lip" || place === "mouth_left" || place === "mouth_right" || place === "below_lip";
  fillShape(m, w, h, LIPS_OUTER.map(pt), nearMouth ? 0 : MASK.lipGrow * iod);
  // The nostrils, except for marks on the nose.
  if (place !== "nose" && place !== "nose_left" && place !== "nose_right") {
    fillDisc(m, w, h, lerp(pt(2), pt(98), 0.6), MASK.nostril * iod);
    fillDisc(m, w, h, lerp(pt(2), pt(327), 0.6), MASK.nostril * iod);
  }
  // Off the face (the hair, the ears, the background), except for marks at its edge.
  if (!/^(temple|jaw|sideburn)_/.test(place)) fillPolygon(m, w, h, ovalOf(pt, MASK.ovalScale), true);
  return m;
}

// --------------------------------------------------------------- fixes --

/**
 * A fix from one face detection, at the look size; null when there is no
 * full face (fewer than 478 points) or it is too small to place a mark on.
 */
export function faceFixOf(lm: readonly Landmark[] | null | undefined, place: string, look: { w: number; h: number }, thumb: number[], t: number): FaceFix | null {
  if (!lm || lm.length < 478) return null;
  const { w, h } = look;
  const pt = (i: number): P => ({ x: lm[i].x * w, y: lm[i].y * h });
  const r = pt(33);
  const l = pt(263);
  const iod = Math.hypot(l.x - r.x, l.y - r.y);
  if (!Number.isFinite(iod) || iod < FACE_FIX.minIod * w) return null;
  const a = pt(168);
  const b = pt(152);
  const u = unit({ x: b.x - a.x, y: b.y - a.y });
  let n = { x: -u.y, y: u.x };
  if ((l.x - a.x) * n.x + (l.y - a.y) * n.y < 0) n = { x: -n.x, y: -n.y };
  const spec = INK_FACE_PLACES[place] ?? { region: "head" as FaceRegion, anchor: null };
  return { t, w, h, iod, a, b, n, mask: maskOf(place, spec.region, pt, iod, w, h), anchor: spec.anchor ? spec.anchor(pt, u, n, iod) : null, thumb };
}

/** How far the point (fractions of the look) is from the midline, in IODs; + = the person's LEFT. */
export function sideOf(fix: FaceFix, p01: { x: number; y: number }): number {
  return ((p01.x * fix.w - fix.a.x) * fix.n.x + (p01.y * fix.h - fix.a.y) * fix.n.y) / fix.iod;
}

/** The point is below the chin (or within 0.1 IOD above it): a mark on the front of the neck (the judge asks only there). */
export function belowChin(fix: FaceFix, p01: { x: number; y: number }): boolean {
  const dx = fix.b.x - fix.a.x;
  const dy = fix.b.y - fix.a.y;
  const len = Math.hypot(dx, dy) || 1;
  const proj = ((p01.x * fix.w - fix.a.x) * dx + (p01.y * fix.h - fix.a.y) * dy) / len;
  return proj >= len - 0.1 * fix.iod;
}

/** The named spot as a box (fractions of the look): the finder's hint. */
export function anchorBox(fix: FaceFix): Box01 | null {
  if (!fix.anchor) return null;
  const half = (FACE_FIX.anchorSide / 2) * fix.iod;
  const x0 = Math.max(0, (fix.anchor.x - half) / fix.w);
  const y0 = Math.max(0, (fix.anchor.y - half) / fix.h);
  const x1 = Math.min(1, (fix.anchor.x + half) / fix.w);
  const y1 = Math.min(1, (fix.anchor.y + half) / fix.h);
  return x1 > x0 && y1 > y0 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}

/**
 * The fix if it still describes this look (young enough, and the picture has not moved since), else null. `maxAgeMs`:
 * the page lets a fix last until the next one is due (a slow phone runs the face model less often than every
 * FACE_FIX.maxAgeMs; between two fixes the finder must not lose the masks and the named spot).
 */
export function freshFix(fix: FaceFix | null | undefined, t: number, thumb: number[], maxAgeMs: number = FACE_FIX.maxAgeMs): FaceFix | null {
  if (!fix) return null;
  if (t - fix.t > maxAgeMs) return null;
  return thumbDiff(fix.thumb, thumb) <= FACE_FIX.maxThumbDiff ? fix : null;
}
